import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Upload,
  Eye,
  Trash2,
  RefreshCw,
  Calendar,
  PhoneCall,
  Layers,
  Clock,
  CalendarClock,
  Loader2,
  CheckCircle2,
  PhoneMissed,
  Voicemail,
  XCircle,
  Filter,
  X,
  Plus,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { createPortal } from "react-dom";
import patientCallingService, {
  PatientServiceLog,
} from "../../../services/patientCalling.service";
import Table from "../../Table/Table";
import CommonPagination from "../../Common/CommonPagination";
import Loading from "../../Common/Loading";
import { Tooltip } from "../../Common/Tooltip";
import ConfirmModal from "../../Common/ConfirmModal";
import CommonDropdown from "../../Common/CommonDropdown";
import styles from "./PatientRecord.module.css";
import ImportPatientModal from "./ImportPatientModal";
import Toast from "../../Common/Toast";
import CreateConfigModal from "./CreateConfigModal";
import ManualAddPatient from "../BatchDetail/ManualAddPatient";
import ScheduleCampaignFlow from "./ScheduleCall/ScheduleCampaignFlow";
import modulesService, { Module } from "../../../services/modules.service";
import authService from "../../../services/auth.service";
// ─── Types ────────────────────────────────────────────────────────────────────

type CallType = "payment_reminder" | "patient_info_gathering";

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  PENDING: "#6366f1",
  SCHEDULED: "#3b82f6",
  IN_PROGRESS: "#f59e0b",
  COMPLETED: "#10b981",
  NO_ANSWER: "#f97316",
  VOICEMAIL: "#a855f7",
  FAILED: "#ef4444",
};

const STATUS_BG: Record<string, string> = {
  PENDING: "#e0e7ff",
  SCHEDULED: "#dbeafe",
  IN_PROGRESS: "#fef3c7",
  COMPLETED: "#dcfce7",
  NO_ANSWER: "#ffedd5",
  VOICEMAIL: "#ede9fe",
  FAILED: "#fee2e2",
};

const CALL_TYPE_LABELS: Record<CallType, string> = {
  payment_reminder: "Payment Reminder",
  patient_info_gathering: "Patient Info Gathering",
};

const CALL_TYPE_OPTIONS: { value: CallType; label: string }[] = [
  { value: "payment_reminder", label: "Payment Reminder" },
  { value: "patient_info_gathering", label: "Patient Info Gathering" },
];

const STAT_STRIP_CONFIG = [
  { label: "Total", statusKey: "", color: "#0c7494", icon: Layers },
  { label: "Pending", statusKey: "PENDING", color: "#6366f1", icon: Clock },
  {
    label: "Scheduled",
    statusKey: "SCHEDULED",
    color: "#3b82f6",
    icon: CalendarClock,
  },
  {
    label: "In Progress",
    statusKey: "IN_PROGRESS",
    color: "#f59e0b",
    icon: Loader2,
  },
  {
    label: "Completed",
    statusKey: "COMPLETED",
    color: "#10b981",
    icon: CheckCircle2,
  },
  {
    label: "No Answer",
    statusKey: "NO_ANSWER",
    color: "#f97316",
    icon: PhoneMissed,
  },
  {
    label: "Voicemail",
    statusKey: "VOICEMAIL",
    color: "#a855f7",
    icon: Voicemail,
  },
  { label: "Failed", statusKey: "FAILED", color: "#ef4444", icon: XCircle },
];

const STATUS_FILTER_OPTIONS = [
  { value: "PENDING", label: "Pending" },
  { value: "SCHEDULED", label: "Scheduled" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "COMPLETED", label: "Completed" },
  { value: "NO_ANSWER", label: "No Answer" },
  { value: "VOICEMAIL", label: "Voicemail" },
  { value: "FAILED", label: "Failed" },
];

// ─── Component ────────────────────────────────────────────────────────────────

const PatientRecordsPage: React.FC = () => {
  const navigate = useNavigate();

  // ── Data ─────────────────────────────────────────────────────────────────
  const [records, setRecords] = useState<PatientServiceLog[]>([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // ── Pagination / search / status filter ──────────────────────────────────
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("PENDING");
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // ── Advanced filters (sidebar panel) ─────────────────────────────────────
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<Record<string, any>>({
    statusId: [],
    callTypes: [],
  });
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({
    statusId: [],
    callTypes: [],
  });
  const activeFilterCount = Object.entries(activeFilters).filter(([, v]) =>
    Array.isArray(v) ? v.length > 0 : !!v,
  ).length;

  const [showConfigModal, setShowConfigModal] = useState(false);
  const [statsCollapsed, setStatsCollapsed] = useState(false);
  // ── Schedule / Delete ─────────────────────────────────────────────────────
  const [showScheduleFlow, setShowScheduleFlow] = useState(false);
  const [recordToDelete, setRecordToDelete] =
    useState<PatientServiceLog | null>(null);
  const [deletingRecord, setDeletingRecord] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddPatient, setShowAddPatient] = useState(false);

  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info" | "warning";
  } | null>(null);

  const [modules, setModules] = useState<Module[]>([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const user = authService.getUser();
        if (!user?.email) return;

        const data = await modulesService.getUserModules(user.email);
        setModules(data);
      } catch (error) {
        console.error("Failed to load permissions:", error);
      } finally {
        setPermissionsLoaded(true);
      }
    };

    loadPermissions();
  }, []);

  const pcModule = modules.find((m) => m.name === "patient_calling");

  const canRead =
    permissionsLoaded && (pcModule?.privileges?.includes("READ") ?? false);

  const canCreate =
    permissionsLoaded && (pcModule?.privileges?.includes("CREATE") ?? false);

  const canDelete =
    permissionsLoaded && (pcModule?.privileges?.includes("DELETE") ?? false);

  const canExecute =
    permissionsLoaded && (pcModule?.privileges?.includes("EXECUTE") ?? false);
  // ── Debounce search ───────────────────────────────────────────────────────
  useEffect(() => {
    const h = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(0);
    }, 400);
    return () => clearTimeout(h);
  }, [search]);

  useEffect(() => {
    loadRecords();
  }, [currentPage, itemsPerPage, debouncedSearch, filterStatus, activeFilters]);

  const loadRecords = async () => {
    setLoading(true);
    try {
      const statusParam =
        filterStatus ||
        (activeFilters.statusId?.length
          ? activeFilters.statusId.join(",")
          : undefined);
      const res = await patientCallingService.getAllRecords({
        page: currentPage + 1,
        page_size: itemsPerPage,
        search: debouncedSearch || undefined,
        call_status: statusParam,
        // call_type:   activeFilters.callTypes?.length ? activeFilters.callTypes.join(',') : undefined,
      });
      setRecords(res.data ?? []);
      setTotal(res.total ?? 0);
      setStatusCounts(res.status_counts ?? {});
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      setIsInitialLoading(false);
    }
  };
  const handleDeleteRecord = (row: PatientServiceLog, e: React.MouseEvent) => {
    e.stopPropagation();
    setRecordToDelete(row);
  };

  const confirmDeleteRecord = async () => {
    if (!recordToDelete) return;
    setDeletingRecord(true);
    try {
      await patientCallingService.deleteServiceLog(recordToDelete.id);
      setRecords((prev) => prev.filter((r) => r.id !== recordToDelete.id));
      setRecordToDelete(null);
    } catch (e: any) {
      setToast({
        type: "error",
        message: e?.message || "Failed to delete record",
      });
    } finally {
      setDeletingRecord(false);
    }
  };

  const handleStatClick = (statusKey: string) => {
    setFilterStatus((prev) => (prev === statusKey ? "" : statusKey));
    setCurrentPage(0);
  };

  const handleApplyFilters = () => {
    setActiveFilters(filters);
    setCurrentPage(0);
    setShowFilters(false);
  };

  const handleResetFilters = () => {
    const reset = { statusId: [], callTypes: [] };
    setFilters(reset);
    setActiveFilters(reset);
    setCurrentPage(0);
    setShowFilters(false);
  };

  // ── Cell helpers ──────────────────────────────────────────────────────────
  const formatDate = (val: string | null): React.ReactNode => {
    if (!val) return <span className={styles.naCell}>N/A</span>;
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return val;
    return d.toLocaleDateString();
  };

  // ── Columns ───────────────────────────────────────────────────────────────
  const recordColumns = [
    {
      key: "patient_name",
      header: "Patient Name",
      render: (value: string) => (
        <span className={styles.patientNameCell}>{value || "—"}</span>
      ),
    },
    {
      key: "phone_number",
      header: "Phone",
      render: (value: string | null) =>
        value ? (
          <span className={styles.phoneCell}>{value}</span>
        ) : (
          <span className={styles.naCell}>N/A</span>
        ),
    },
    {
      key: "call_type",
      header: "Call Type",
      render: (value: string | null) =>
        value ? (
          <span
            className={`${styles.badge} ${value === "payment_reminder" ? styles.badgeGreen : styles.badgePurple}`}
          >
            {CALL_TYPE_LABELS[value as CallType] || value}
          </span>
        ) : (
          <span className={styles.naCell}>—</span>
        ),
    },
    {
      key: "dos",
      header: "DOS",
      render: (value: string | null) => formatDate(value),
    },
    {
      key: "insurance_name",
      header: "Insurance",
      render: (value: string | null) =>
        value || <span className={styles.naCell}>N/A</span>,
    },
    {
      key: "call_status",
      header: "Call Status",
      render: (value: string | null) => {
        const status = value || "PENDING";
        const fg = STATUS_COLORS[status] || "#6b7280";
        const bg = STATUS_BG[status] || "#f3f4f6";
        return (
          <span
            className={styles.statusPill}
            style={{ background: bg, color: fg, borderColor: `${fg}33` }}
          >
            <span className={styles.statusPillDot} style={{ background: fg }} />
            {status.replace(/_/g, " ")}
          </span>
        );
      },
    },
    {
      key: "last_called_at",
      header: "Last Called",
      render: (value: string | null) => formatDate(value),
    },
    {
      key: "created_at",
      header: "Created",
      render: (value: string | null) => formatDate(value),
    },
    {
      key: "actions",
      header: "Actions",
      render: (_: any, row: PatientServiceLog) => (
        <div className={styles.rowActions} onClick={(e) => e.stopPropagation()}>
          <Tooltip content="View Detail">
            <button
              className={`${styles.actionBtn} ${styles.view}`}
              onClick={() => navigate(`/patient-calling/records/${row.id}`)}
            >
              <Eye size={14} />
            </button>
          </Tooltip>
          <Tooltip content="Delete">
            {canDelete && (
              <button
                className={`${styles.actionBtn} ${styles.delete}`}
                onClick={(e) => handleDeleteRecord(row, e)}
              >
                <Trash2 size={14} />
              </button>
            )}
          </Tooltip>
        </div>
      ),
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className={styles.page}>
      {toast && (
        <div className={styles.toastWrap}>
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        </div>
      )}
      {/* ══════════════════════════════════════════════════════════════
                COLLAPSIBLE HEADER REGION
                Wraps page header card + stat chips so the entire upper part
                animates smoothly via grid-template-rows: 1fr ↔ 0fr.
            ══════════════════════════════════════════════════════════════ */}
      <div
        className={`${styles.collapsibleHeader} ${
          statsCollapsed ? styles.collapsibleHeaderCollapsed : ""
        }`}
      >
        <div className={styles.collapsibleHeaderInner}>
          <div className={styles.pageHeaderCard}>
            <div className={styles.pageHeaderLeft}>
              <div className={styles.pageIcon}>
                <PhoneCall size={20} />
              </div>
              <div>
                <h1 className={styles.pageTitle}>Patient Records</h1>
                <p className={styles.pageSubtitle}>
                  {total} record{total !== 1 ? "s" : ""} total
                </p>
              </div>
            </div>

            <div className={styles.actionRow}>
              {/* Refresh — ghost icon button (spins while loading) */}
              <button
                className={styles.ghostIconBtn}
                onClick={loadRecords}
                title="Refresh"
                type="button"
                disabled={loading}
              >
                <RefreshCw
                  size={14}
                  className={loading ? styles.spinning : ""}
                />
              </button>

              {/* Secondary actions */}
              <button
                className={styles.actionPillBtn}
                onClick={() => setShowImportModal(true)}
                type="button"
              >
                <Upload size={14} /> Import File
              </button>
              <button
                className={styles.actionPillBtn}
                onClick={() => setShowConfigModal(true)}
                type="button"
              >
                <Layers size={14} /> Create Config
              </button>

              {/* Primary action */}
              {canCreate && (
                <button
                  className={styles.addBtn}
                  onClick={() => setShowScheduleFlow(true)}
                >
                  <Calendar size={14} /> Schedule Calls
                </button>
              )}
            </div>
          </div>

          {/* ══════════════════════════════════════════════════════════════
                STAT CHIPS — replaces ClientManagement statsGrid
                8 chips, clickable to filter by status
            ══════════════════════════════════════════════════════════════ */}
          <div className={styles.statsGrid}>
            {STAT_STRIP_CONFIG.map((s) => {
              const isTotal = s.statusKey === "";
              const isActive = !isTotal && filterStatus === s.statusKey;
              const Icon = s.icon;
              return (
                <div
                  key={s.label}
                  className={`${styles.statChip} ${isActive ? styles.statChipActive : ""} ${isTotal ? styles.statChipTotal : styles.statChipFilter}`}
                  style={{ ["--chip-color" as any]: s.color }}
                  onClick={() => !isTotal && handleStatClick(s.statusKey)}
                >
                  <div className={styles.statChipIcon}>
                    <Icon size={15} strokeWidth={2.25} />
                  </div>
                  <div className={styles.statChipBody}>
                    <span className={styles.statChipValue}>
                      {isTotal ? total : (statusCounts[s.statusKey] ?? 0)}
                    </span>
                    <span className={styles.statChipLabel}>{s.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Collapse / expand handle (sits on the dashed separator line) */}
      <div
        className={`${styles.statsToggleBar} ${statsCollapsed ? styles.statsToggleBarCollapsed : ""}`}
      >
        <button
          type="button"
          className={styles.statsToggleBtn}
          onClick={() => setStatsCollapsed((c) => !c)}
          title={statsCollapsed ? "Show stats" : "Hide stats"}
          aria-label={statsCollapsed ? "Show stats" : "Hide stats"}
          aria-expanded={!statsCollapsed}
        >
          {statsCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
        </button>
      </div>

      {/* ══════════════════════════════════════════════════════════════
                TABLE CARD
                Header:  "Patient Records" title left | search + Filters right
                Body:    Table / empty state / loading overlay
                Matches ClientManagement.tableSection + tableHeader exactly.
            ══════════════════════════════════════════════════════════════ */}
      <div className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <div className={styles.searchWrap}>
            <Search size={15} className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search by name, phone, status..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className={styles.tableHeaderTools}>
            <button
              className={`${styles.filterBtn} ${activeFilterCount > 0 ? styles.active : ""}`}
              onClick={() => setShowFilters(true)}
            >
              <Filter size={14} />
              Filters
              {activeFilterCount > 0 && (
                <span className={styles.filterBadge}>{activeFilterCount}</span>
              )}
            </button>
            <button
              className={styles.addBtn}
              onClick={() => setShowAddPatient(true)}
              type="button"
            >
              <Plus size={14} /> Add Patient
            </button>
          </div>
        </div>

        {/* Body */}
        <div className={styles.tableBody}>
          {/* {isInitialLoading ? (
                        <Loading message="Loading records..." />
                    ) : records.length === 0 ? (
                        <div className={styles.tableEmpty}>
                            <PhoneCall size={48} className={styles.tableEmptyIcon} />
                            <h3 className={styles.tableEmptyTitle}>No patient records found</h3>
                            <p className={styles.tableEmptyMessage}>
                                {search
                                    ? 'Try adjusting your search criteria'
                                    : 'Get started by importing patient records'}
                            </p>
                        </div>
                    ) : ( */}
          {!canRead ? (
            <div>No permission to view records</div>
          ) : (
            <Table
              columns={recordColumns}
              data={records}
              onRowClick={(row) =>
                navigate(`/patient-calling/records/${row.id}`)
              }
              maxHeight={
                statsCollapsed ? "calc(100vh - 250px)" : "calc(100vh - 360px)"
              }
            />
          )}

          {loading && !isInitialLoading && (
            <div className={styles.loadingOverlay}>
              <Loading message="Updating..." />
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      <CommonPagination
        show={total > 0}
        pageCount={Math.ceil(total / itemsPerPage)}
        currentPage={currentPage}
        totalItems={total}
        itemsPerPage={itemsPerPage}
        onPageChange={(data) => setCurrentPage(data.selected)}
        onItemsPerPageChange={(items) => {
          setItemsPerPage(items);
          setCurrentPage(0);
        }}
      />
      {showConfigModal && (
        <CreateConfigModal onClose={() => setShowConfigModal(false)} />
      )}
      {showImportModal && (
        <ImportPatientModal
          onClose={() => setShowImportModal(false)}
          onImported={() => loadRecords()}
        />
      )}

      {showAddPatient && (
        <ManualAddPatient
          onClose={() => setShowAddPatient(false)}
          onSaved={() => {
            setShowAddPatient(false);
            loadRecords();
          }}
        />
      )}
      {/* Schedule Drawer */}
      {showScheduleFlow && (
        <ScheduleCampaignFlow
          onClose={() => setShowScheduleFlow(false)}
          onScheduled={() => {
            setShowScheduleFlow(false);
            loadRecords();
          }}
        />
      )}

      {/* Delete Confirm */}
      <ConfirmModal
        isOpen={!!recordToDelete}
        onClose={() => !deletingRecord && setRecordToDelete(null)}
        onConfirm={confirmDeleteRecord}
        title="Remove Patient"
        message={`Remove "${recordToDelete?.patient_name ?? "this patient"}"? This cannot be undone.`}
        confirmText="Remove"
        type="danger"
        loading={deletingRecord}
      />

      {/* ══════════════════════════════════════════════════════════════
                FILTER SIDE PANEL
                Slide-in from right, same as ClientManagement filterModal.
                Uses .filterOverlay / .filterModal / .filterHeader /
                .filterBody / .filterGroup / .filterFooter / .resetBtn / .applyBtn
            ══════════════════════════════════════════════════════════════ */}
      {showFilters &&
        createPortal(
          <div
            className={styles.filterOverlay}
            onClick={() => setShowFilters(false)}
          >
            <div
              className={styles.filterModal}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.filterHeader}>
                <h3>
                  <Filter size={18} />
                  Advanced Filters
                </h3>
                <button
                  className={styles.closeFilterBtn}
                  onClick={() => setShowFilters(false)}
                >
                  <X size={20} />
                </button>
              </div>

              <div className={styles.filterBody}>
                <div className={styles.filterGroup}>
                  <label>Call Status</label>
                  <CommonDropdown
                    options={STATUS_FILTER_OPTIONS}
                    value={filters.statusId}
                    onChange={(val) =>
                      setFilters((prev) => ({ ...prev, statusId: val }))
                    }
                    placeholder="Select status..."
                    isMulti={true}
                    size="md"
                  />
                </div>
                <div className={styles.filterGroup}>
                  <label>Call Type</label>
                  <CommonDropdown
                    options={CALL_TYPE_OPTIONS}
                    value={filters.callTypes}
                    onChange={(val) =>
                      setFilters((prev) => ({ ...prev, callTypes: val }))
                    }
                    placeholder="Select call type..."
                    isMulti={true}
                    size="md"
                  />
                </div>
              </div>

              <div className={styles.filterFooter}>
                <button
                  className={styles.resetBtn}
                  onClick={handleResetFilters}
                  disabled={activeFilterCount === 0}
                >
                  Reset
                </button>
                <button
                  className={styles.applyBtn}
                  onClick={handleApplyFilters}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default PatientRecordsPage;
